import React, { useRef, useEffect, useState } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

interface VideoPlayerProps {
  src: string;
  className?: string;
  poster?: string;
}

export default function VideoPlayer({ src, className = '', poster }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set up video properties - muted by default for autoplay
    video.muted = true;
    video.loop = true;
    video.volume = 0.7; // Set default volume to 70%
    setIsMuted(true); // Update state to reflect muted status

    // Auto-load and attempt autoplay (muted)
    video.load();

    const handleLoadedData = async () => {
      try {
        // Attempt to autoplay the video (muted)
        await video.play();
        setIsPlaying(true);
      } catch (error) {
        console.log('Autoplay prevented by browser:', error);
        setIsPlaying(false);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent play/pause when clicking mute button
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      togglePlayPause();
    }
  };

  return (
    <div
      className={`relative group cursor-pointer ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
      onClick={togglePlayPause}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={isPlaying ? 'Pause video' : 'Play video'} data-id="g709wgpyl" data-path="src/components/VideoPlayer.tsx">

      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="w-full h-auto rounded-2xl"
        playsInline
        loop
        muted
        autoPlay
        preload="metadata" data-id="dvszsx9ss" data-path="src/components/VideoPlayer.tsx" />

      {/* Mute/Unmute Button - Top Left Corner */}
      <div className="absolute top-4 left-4 z-10" data-id="vctlzs73x" data-path="src/components/VideoPlayer.tsx">
        <button
          onClick={toggleMute}
          className={`bg-black/60 backdrop-blur-sm rounded-full p-2 shadow-lg transition-all duration-300 hover:bg-black/80 ${
          showControls ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`
          }
          aria-label={isMuted ? 'Unmute video' : 'Mute video'} data-id="5tryybqr8" data-path="src/components/VideoPlayer.tsx">

          {isMuted ?
          <VolumeX className="w-5 h-5 text-white" data-id="1hwxempkg" data-path="src/components/VideoPlayer.tsx" /> :

          <Volume2 className="w-5 h-5 text-white" data-id="wfz4m8htf" data-path="src/components/VideoPlayer.tsx" />
          }
        </button>
      </div>
      
      {/* Custom Controls Overlay */}
      <div
        className={`absolute inset-0 bg-black/20 rounded-2xl flex items-center justify-center transition-opacity duration-300 ${
        showControls ? 'opacity-100' : 'opacity-0'}`
        } data-id="nnu7lzrsm" data-path="src/components/VideoPlayer.tsx">

        <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg transition-transform duration-200 hover:scale-110" data-id="iknmr3du0" data-path="src/components/VideoPlayer.tsx">
          {isPlaying ?
          <Pause className="w-8 h-8 text-gray-800" aria-hidden="true" data-id="12rbaelaj" data-path="src/components/VideoPlayer.tsx" /> :

          <Play className="w-8 h-8 text-gray-800 pl-1" aria-hidden="true" data-id="mwmom072l" data-path="src/components/VideoPlayer.tsx" />
          }
        </div>
      </div>

      {/* Screen Reader Support */}
      <div className="sr-only" data-id="ls5egph5k" data-path="src/components/VideoPlayer.tsx">
        {isPlaying ?
        'Video is currently playing. Click to pause.' :
        'Video is currently paused. Click to play.'
        }
        {isMuted ? ' Audio is muted.' : ' Audio is enabled.'}
      </div>
    </div>);

}